import { useState } from 'react';
import config from '../config/config';
import { useAuth } from '../contexts/AuthContext';
import authHeader from '../config/authHeader';

const useFetchSites = () => {
    const { token, logout } = useAuth();

    const [sites, setSites] = useState([]);
    const [countSites, setCountSites] = useState(0);
    const [site, setSite] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchSites = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getSites`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) {
                throw new Error('Erreur réseau lors de la récupération des sites');
            }

            const data = await response.json();
            setSites(data);
            return data[0];
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCountSites = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getCountSites`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) {
                throw new Error('Erreur réseau lors de la récupération du compteur des sites');
            }

            const { count } = await response.json();
            setCountSites(count);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchSiteByUUID = async (uuid) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getSiteByUUID?uuid=${uuid}`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) {
                throw new Error('Erreur réseau lors de la récupération du site par UUID');
            }

            const data = await response.json();
            setSite(data);
            return data;
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const createSite = async (siteData) => {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append("sitePayload", JSON.stringify(siteData));

        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_createSite`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) {
                throw new Error('Erreur réseau lors de la création du site');
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const updateSite = async (siteData) => {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append("sitePayload", JSON.stringify(siteData));

        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_updateSite`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) {
                const message = `Erreur (${response.status}) lors de la mise à jour du site`;

                const err = new Error(message);
                err.status = response.status;

                throw err;
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        sites,
        countSites,
        site,
        fetchSites,
        fetchCountSites,
        fetchSiteByUUID,
        createSite,
        updateSite,
        loading,
        error
    };
};

export default useFetchSites;